import Image from 'next/image';
import {
  AspectRatio,
  Button,
  Flex,
  Heading,
  Icon,
  Stack,
  Text,
  useDisclosure,
  Box,
  Fade
} from '@chakra-ui/react';
import Link from 'next/link';
import PlayIcon from 'remixicon-react/PlayCircleFillIcon';
import ArrowRightUp from 'remixicon-react/ArrowRightUpLineIcon';
import ArrowRightLineIcon from 'remixicon-react/ArrowRightLineIcon';
import { useEffect, useState } from 'react';
import {
  DemoModal,
  LayoutContainer,
  GridPatternDark
} from '@components/common';
import { analytics } from 'pages';

function HoverButton({
  isShowing,
  onOpen
}: {
  isShowing: boolean;
  onOpen: () => void;
}): JSX.Element {
  const [hidden, setHide] = useState<boolean>(false);

  useEffect(() => {
    if (isShowing) {
      setHide(false);
    }
  }, [isShowing]);

  return (
    <Box
      cursor="pointer"
      pos="relative"
      zIndex={100}
      display={hidden ? 'none' : 'inline-flex'}
      onClick={() => {
        setHide(true);
        onOpen();
        analytics.track('Marketing_Home_Play Video Clicked', {
          surface: 'Marketing',
          location: 'Home'
        });
      }}
    >
      <Box
        borderRadius="50%"
        h="102px"
        w="102px"
        transform={isShowing ? 'scale(1.05)' : ''}
        pos="relative"
      >
        <Icon
          p="6px"
          as={PlayIcon}
          boxSize={'102px'}
          transform={isShowing ? 'scale(1.05)' : ''}
          borderRadius="50%"
          color={isShowing ? 'primary.500' : 'primary.700'}
          zIndex={1}
          transition="all 0.2s"
        />
        <Box
          // bg="rgb(108,71,255, .10)"
          bg="white"
          h={50}
          w={50}
          borderRadius="50%"
          pos="absolute"
          left={7}
          bottom={6}
          zIndex={-1}
        ></Box>
        <Box
          bg="rgb(108,71,255, .10)"
          h={102}
          w={102}
          borderRadius="50%"
          pos="absolute"
          left={0}
          bottom={0}
          zIndex={-1}
        ></Box>
      </Box>

      <Fade in={isShowing}>
        <AspectRatio
          zIndex={2}
          h={10}
          w={10}
          pos="absolute"
          top="14"
          right="-4"
          borderRadius="50%"
          border="white 2px solid"
        >
          <Image
            src="/avatars/james.webp"
            alt=""
            fill
            style={{ borderRadius: '50%' }}
          />
        </AspectRatio>
        <Box
          bg="white"
          h={16}
          w={80}
          borderRadius={24}
          shadow={isShowing ? 'lg' : 'base'}
          pos="absolute"
          top="100%"
          left="-60%"
          zIndex={-1}
        >
          <Flex flexDir={'column'} alignItems="center" justifyContent="center">
            <Text fontSize="xs" fontWeight={'semibold'} mt={4}>
              Watch James build a Clerk app
            </Text>
            <Flex justifyContent="space-between" alignItems="center" w={'42%'}>
              <Text color="gray.500" fontSize="xs">
                5 min
              </Text>
              <Text color="primary.700" fontSize="sm">
                Watch now <Icon as={ArrowRightLineIcon} />
              </Text>
            </Flex>
          </Flex>
        </Box>
      </Fade>
    </Box>
  );
}

export function Hero(): JSX.Element {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isShowing, setIsShowing] = useState<boolean>(false);
  return (
    <LayoutContainer verticalPadding="sm" pos="relative">
      <AspectRatio
        ratio={1400 / 1190}
        w="1400px"
        pos="absolute"
        top="200px"
        pointerEvents="none"
        draggable={false}
        userSelect="none"
      >
        <GridPatternDark />
      </AspectRatio>
      <DemoModal
        isOpen={isOpen}
        onClose={() => {
          setIsShowing(true);
          onClose();
        }}
      />
      <Stack align="center" textAlign="center" spacing={8}>
        <Heading as="h1" size="h1">
          More than authentication.
          <br />
          <Text as="span" color="primary.500">
            Complete User Management
          </Text>
        </Heading>

        <Text fontSize="xl">
          Clerk is more than a “sign-in box”. Integrate complete user management
          UIs and APIs,<br></br> purpose-built for React, Next.js, and the
          Modern Web.
        </Text>

        <Flex
          flexDir={{ base: 'column', sm: 'row' }}
          align="center"
          justify="center"
          w="full"
          gap={4}
          pos="relative"
        >
          <Link
            legacyBehavior
            href={process.env.NEXT_PUBLIC_DASHBOARD_URL as string}
            passHref
          >
            <Button
              as="a"
              w={{ base: 'full', sm: 56 }}
              cursor="pointer"
              variant="base"
              h={12}
              fontSize="md"
              zIndex={100}
              onClick={() =>
                analytics.track(
                  'Marketing_Home_Start Building for Free Button Clicked',
                  { surface: 'Marketing', location: 'Home' }
                )
              }
              rightIcon={<Icon as={ArrowRightUp} boxSize={5} />}
            >
              Start building for free
            </Button>
          </Link>

          <Button
            w={{ base: 'full', sm: 56 }}
            cursor="pointer"
            onClick={() => {
              analytics.track('Marketing_Home_Watch Demo Button  Clicked', {
                surface: 'Marketing',
                location: 'Home'
              });
              onOpen();
            }}
            h={14}
            variant="ghost"
            fontSize="md"
            zIndex={100}
            color="primary.500"
            _hover={{
              color: 'primary.700'
            }}
          >
            <Icon as={PlayIcon} boxSize={12} mx="16px" />
            <Flex
              flexDir={'column'}
              alignItems="flex-start"
              justifyContent="space-between"
            >
              <Text>Watch demo</Text>
              <Text color="gray.500" fontSize="xs">
                5 min
              </Text>
            </Flex>
          </Button>
        </Flex>

        <Box
          onMouseEnter={() => setIsShowing(true)}
          onMouseLeave={() => setIsShowing(false)}
          pos="relative"
        >
          <Box pos="absolute" top="40%" left="0%" right="0%">
            <HoverButton isShowing={isShowing} onOpen={onOpen} />
          </Box>
          <AspectRatio
            mt={{ md: '-60px' }}
            mb={{ md: '-200px' }}
            width="100vw"
            pos="relative"
            zIndex="1"
            maxW="1400px"
          >
            <Box>
              <Image
                src="/images/home/new-hero.png"
                alt=""
                width={2695}
                height={1614}
                priority
              />
            </Box>
          </AspectRatio>
        </Box>
      </Stack>
    </LayoutContainer>
  );
}
